import { useApi } from "@backstage/core-plugin-api";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  makeStyles,
  Paper,
  Tooltip,
  Typography,
} from "@material-ui/core";
import type { Theme } from "@material-ui/core/styles";
import { useTheme } from "@material-ui/core/styles";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import { useCallback, useEffect, useState } from "react";

import { explorerApiRef } from "../../api/ExplorerApi";
import {
  type CloudHealthAssetAttribute,
  type CloudHealthAssetRelation,
  type ExplorerAssetApiDetail,
} from "../../api/types";

import { StatusChip } from "./statusChip";
import { type AssetTableRow } from "./types";

const DRAWER_TITLE_ID = "asset-info-drawer-title";
const NODE_REF_VISIBLE_LENGTH = 56;
const RELATION_ITEM_PREVIEW_LIMIT = 3;

const MONOSPACE_FONT =
  'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace';

const useStyles = makeStyles((theme) => ({
  drawerPaper: {
    width: 460,
    maxWidth: "100vw",
  },
  content: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    padding: theme.spacing(3, 3, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
  },
  eyebrow: {
    color: theme.palette.text.secondary,
    fontSize: "0.75rem",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
  },
  body: {
    flex: 1,
    overflow: "auto",
    padding: theme.spacing(2.5, 3),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  section: {
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(1.5),
  },
  sectionTitle: {
    marginBottom: theme.spacing(1),
    fontWeight: 600,
  },
  summaryList: {
    margin: 0,
    display: "grid",
    gridTemplateColumns: "minmax(120px, auto) 1fr",
    rowGap: theme.spacing(1),
    columnGap: theme.spacing(1.25),
  },
  term: {
    margin: 0,
    color: theme.palette.text.secondary,
    fontWeight: 500,
  },
  value: {
    margin: 0,
    minWidth: 0,
    overflowWrap: "anywhere",
    wordBreak: "normal",
  },
  statusValue: {
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  nodeRefRow: {
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
  },
  nodeRefText: {
    fontFamily: MONOSPACE_FONT,
    fontSize: "0.8rem",
  },
  footer: {
    padding: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
  },
  // ─── "All CloudHealth attributes" collapsible section ─────────────────
  accordion: {
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.default,
    boxShadow: "none",
    "&:before": {
      display: "none",
    },
    "&$accordionExpanded": {
      margin: 0,
    },
  },
  accordionExpanded: {},
  accordionSummary: {
    padding: theme.spacing(1.5),
    minHeight: 0,
    "& .MuiAccordionSummary-content": {
      margin: 0,
      alignItems: "center",
      gap: theme.spacing(1),
      flex: 1,
      minWidth: 0,
    },
    "& .MuiAccordionSummary-expandIcon": {
      color: theme.palette.text.secondary,
      padding: 4,
    },
  },
  accordionTitle: {
    fontWeight: 600,
    fontSize: "0.95rem",
    flex: 1,
    minWidth: 0,
  },
  accordionCount: {
    color: theme.palette.text.secondary,
    fontSize: "0.7rem",
    fontFamily: MONOSPACE_FONT,
    letterSpacing: "0.02em",
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  accordionSpinner: {
    flexShrink: 0,
    color: theme.palette.text.secondary,
  },
  accordionDetails: {
    padding: theme.spacing(0, 2, 2),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.75),
  },
  attributesIntro: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(-0.25),
  },
  subSectionLabel: {
    display: "block",
    color: theme.palette.text.secondary,
    fontSize: "0.65rem",
    fontWeight: 700,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    marginBottom: theme.spacing(0.75),
  },
  attributesList: {
    margin: 0,
    display: "grid",
    gridTemplateColumns: "minmax(150px, auto) 1fr",
    rowGap: theme.spacing(0.75),
    columnGap: theme.spacing(1.25),
  },
  attributeKey: {
    margin: 0,
    fontFamily: MONOSPACE_FONT,
    fontSize: "0.78rem",
    color: theme.palette.text.secondary,
    fontWeight: 500,
    overflowWrap: "anywhere",
    wordBreak: "normal",
  },
  attributeValue: {
    margin: 0,
    fontFamily: MONOSPACE_FONT,
    fontSize: "0.82rem",
    color: theme.palette.text.primary,
    whiteSpace: "pre-wrap",
    overflowWrap: "anywhere",
    wordBreak: "normal",
    minWidth: 0,
  },
  attributeJson: {
    display: "block",
    margin: 0,
    fontFamily: MONOSPACE_FONT,
    fontSize: "0.78rem",
    lineHeight: 1.5,
    color: theme.palette.text.primary,
    whiteSpace: "pre-wrap",
    overflowWrap: "anywhere",
    wordBreak: "normal",
    minWidth: 0,
    padding: theme.spacing(0.75, 1),
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    maxHeight: 160,
    overflow: "auto",
  },
  emDash: {
    color: theme.palette.text.disabled,
  },
  relationsList: {
    margin: 0,
    padding: 0,
    listStyle: "none",
    display: "flex",
    flexDirection: "column",
  },
  relationItem: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.5),
    padding: theme.spacing(1, 0),
    borderBottom: `1px dashed ${theme.palette.divider}`,
    "&:first-child": {
      paddingTop: 0,
    },
    "&:last-child": {
      paddingBottom: 0,
      borderBottom: "none",
    },
  },
  relationHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
  relationName: {
    color: theme.palette.text.primary,
    fontSize: "0.85rem",
    fontWeight: 500,
    fontFamily: MONOSPACE_FONT,
    overflowWrap: "anywhere",
    wordBreak: "normal",
    minWidth: 0,
  },
  relationBadge: {
    display: "inline-block",
    fontFamily: MONOSPACE_FONT,
    fontSize: "0.65rem",
    fontWeight: 600,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    padding: "1px 8px",
    borderRadius: 10,
    border: "1px solid currentColor",
    backgroundColor: "transparent",
    lineHeight: 1.5,
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  relationItemCount: {
    color: theme.palette.text.secondary,
    fontSize: "0.7rem",
    fontFamily: MONOSPACE_FONT,
  },
  relationItemList: {
    margin: 0,
    padding: 0,
    listStyle: "none",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.25),
  },
  relationItemLine: {
    fontFamily: MONOSPACE_FONT,
    fontSize: "0.78rem",
    color: theme.palette.text.primary,
    paddingLeft: theme.spacing(1.25),
    borderLeft: `2px solid ${theme.palette.divider}`,
    overflowWrap: "anywhere",
    wordBreak: "normal",
  },
  relationItemMore: {
    color: theme.palette.text.secondary,
    fontSize: "0.7rem",
    fontStyle: "italic",
    paddingLeft: theme.spacing(1.25),
    fontFamily: MONOSPACE_FONT,
  },
  apiError: {
    padding: theme.spacing(1, 1.25),
    color: theme.palette.error.dark,
    backgroundColor: theme.palette.error.light,
    borderRadius: theme.shape.borderRadius,
    fontSize: "0.8rem",
    whiteSpace: "pre-wrap",
  },
  apiEmpty: {
    color: theme.palette.text.secondary,
    fontSize: "0.85rem",
    fontStyle: "italic",
    margin: 0,
  },
  loadingState: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.25),
    padding: theme.spacing(0.5, 0),
  },
}));

function scopeLabelFromProvider(
  provider?: AssetTableRow["asset"]["cloud"],
): string {
  if (provider === "GCP") {
    return "Project";
  }
  if (provider === "Azure") {
    return "Resource group";
  }
  return "Account";
}

function truncateNodeRef(
  nodeRef: string,
  maxLength = NODE_REF_VISIBLE_LENGTH,
): string {
  if (nodeRef.length <= maxLength) {
    return nodeRef;
  }

  const visibleChars = maxLength - 1;
  const head = Math.ceil(visibleChars / 2);
  const tail = Math.floor(visibleChars / 2);
  return `${nodeRef.slice(0, head)}…${nodeRef.slice(-tail)}`;
}

function locationOrResourceGroup(row?: AssetTableRow): string {
  if (!row) {
    return "Not available";
  }

  const parts =
    row.asset.cloud === "Azure"
      ? [row.asset.assetLocation, row.asset.resourceGroup]
      : [row.asset.assetLocation];

  const unique = [
    ...new Set(parts.filter((part): part is string => Boolean(part))),
  ];
  return unique.length > 0 ? unique.join(" · ") : "Not available";
}

function isEmptyValue(value: unknown): boolean {
  return value === null || value === undefined || value === "";
}

function formatRelationItem(item: unknown): string {
  if (item === null || item === undefined) {
    return "—";
  }
  if (typeof item === "string") {
    return item;
  }
  if (typeof item === "number" || typeof item === "boolean") {
    return String(item);
  }
  return JSON.stringify(item);
}

/**
 * Deterministically pick a theme color for a relation `object_type` so the
 * badge hints at the type without being garish. Hashing the string keeps
 * related object_types visually consistent across renders.
 */
function getBadgeColor(theme: Theme, objectType: string): string {
  const palette: string[] = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.error?.main ?? theme.palette.primary.main,
    theme.palette.warning?.main ?? theme.palette.primary.main,
    theme.palette.info?.main ?? theme.palette.primary.main,
    theme.palette.success?.main ?? theme.palette.primary.main,
  ];

  let hash = 0;
  for (let i = 0; i < objectType.length; i++) {
    hash = (hash * 31 + objectType.charCodeAt(i)) | 0;
  }
  return palette[Math.abs(hash) % palette.length];
}

function renderAttributeValue(
  value: unknown,
  classes: Record<string, string>,
): JSX.Element {
  if (isEmptyValue(value)) {
    return (
      <Box component="dd" className={classes.attributeValue}>
        <span className={classes.emDash}>—</span>
      </Box>
    );
  }
  if (typeof value === "object") {
    let serialized: string;
    try {
      serialized = JSON.stringify(value, null, 2);
    } catch {
      serialized = String(value);
    }
    return (
      <Box component="dd" className={classes.attributeValue}>
        <pre className={classes.attributeJson}>{serialized}</pre>
      </Box>
    );
  }
  return (
    <Typography component="dd" className={classes.attributeValue}>
      {String(value)}
    </Typography>
  );
}

function renderRelationItems(
  relation: CloudHealthAssetRelation,
  classes: Record<string, string>,
): JSX.Element {
  const items = relation.items ?? [];
  if (items.length === 0) {
    return (
      <Box>
        <span className={classes.emDash}>—</span>
      </Box>
    );
  }

  const visible = items.slice(0, RELATION_ITEM_PREVIEW_LIMIT);
  const remaining = items.length - visible.length;

  return (
    <Box>
      <span className={classes.relationItemCount}>
        {items.length} {items.length === 1 ? "item" : "items"}
      </span>
      <ul className={classes.relationItemList}>
        {visible.map((item) => {
          const formatted = formatRelationItem(item);
          return (
            <li
              key={`${relation.name}:${formatted}`}
              className={classes.relationItemLine}
            >
              {formatted}
            </li>
          );
        })}
        {remaining > 0 && (
          <li className={classes.relationItemMore}>+{remaining} more</li>
        )}
      </ul>
    </Box>
  );
}

type CloudHealthApiFieldsSectionProps = {
  nodeRef: string;
  api: typeof explorerApiRef;
  initialExpanded?: boolean;
};

function CloudHealthApiFieldsSection({
  nodeRef,
  api,
  initialExpanded = false,
}: CloudHealthApiFieldsSectionProps) {
  const classes = useStyles();
  const theme = useTheme();
  const explorerApi = useApi(api);

  const [expanded, setExpanded] = useState(initialExpanded);
  const [data, setData] = useState<ExplorerAssetApiDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Reset cached state when the user navigates to a different asset so we
  // never display attributes for the wrong nodeRef. The dep is a trigger
  // rather than a consumed value — the effect intentionally re-runs whenever
  // the asset changes, even though the body does not read `nodeRef`.
  useEffect(() => {
    setData(null);
    setError(null);
    setLoading(false);
    setExpanded(initialExpanded);
    void nodeRef;
  }, [nodeRef, initialExpanded]);

  // Lazy fetch: only on the first expand (and again if nodeRef changes while
  // expanded). Cached in state — collapsing/re-expanding will not refetch.
  useEffect(() => {
    if (!expanded) {
      return;
    }
    if (data !== null || error !== null) {
      return;
    }

    let cancelled = false;
    setLoading(true);
    explorerApi
      .getAssetDetails(nodeRef)
      .then((result) => {
        if (!cancelled) {
          setData(result);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(
            err instanceof Error
              ? err.message
              : "Failed to load CloudHealth API details.",
          );
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [explorerApi, nodeRef, expanded, data, error]);

  const handleChange = useCallback((_event: unknown, isExpanded: boolean) => {
    setExpanded(isExpanded);
  }, []);

  // The brief moment between `expanded=true` and the fetch effect committing
  // `loading=true` should still read as "loading" to avoid flashing an empty
  // body.
  const isInitialLoading = loading || (data === null && error === null);
  const hasContent =
    data !== null && (data.attributes.length > 0 || data.relations.length > 0);

  return (
    <Accordion
      square
      expanded={expanded}
      onChange={handleChange}
      className={classes.accordion}
      classes={{ expanded: classes.accordionExpanded }}
    >
      <AccordionSummary
        className={classes.accordionSummary}
        expandIcon={<ExpandMoreIcon fontSize="small" />}
        aria-controls="cloudhealth-api-fields-content"
        id="cloudhealth-api-fields-header"
      >
        <Typography className={classes.accordionTitle}>
          CloudHealth attributes
        </Typography>
        {loading && (
          <CircularProgress
            size={20}
            className={classes.accordionSpinner}
            aria-label="Loading CloudHealth attributes"
          />
        )}
        {data !== null && hasContent && !loading && (
          <span className={classes.accordionCount}>
            {data.attributes.length} attr · {data.relations.length} rel
          </span>
        )}
      </AccordionSummary>
      <AccordionDetails
        className={classes.accordionDetails}
        aria-busy={isInitialLoading}
      >
        {isInitialLoading ? (
          <Box className={classes.loadingState}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="textSecondary">
              Loading CloudHealth attributes…
            </Typography>
          </Box>
        ) : error ? (
          <Typography role="alert" variant="body2" className={classes.apiError}>
            {error}
          </Typography>
        ) : data ? (
          <>
            <Typography variant="body2" className={classes.attributesIntro}>
              Raw attributes are loaded only when you open this section, so long
              asset lists stay fast.
            </Typography>
            <Box component="section" aria-labelledby="chql-attributes-label">
              <Typography
                component="span"
                variant="caption"
                id="chql-attributes-label"
                className={classes.subSectionLabel}
              >
                Attributes
              </Typography>
              {data.attributes.length > 0 ? (
                <Box component="dl" className={classes.attributesList}>
                  {data.attributes.map((attr: CloudHealthAssetAttribute) => (
                    <Box key={attr.name} component="div" display="contents">
                      <Typography
                        component="dt"
                        className={classes.attributeKey}
                      >
                        {attr.name}
                      </Typography>
                      {renderAttributeValue(attr.value, classes)}
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" className={classes.apiEmpty}>
                  No attributes returned.
                </Typography>
              )}
            </Box>

            <Box component="section" aria-labelledby="chql-relations-label">
              <Typography
                component="span"
                variant="caption"
                id="chql-relations-label"
                className={classes.subSectionLabel}
              >
                Relations
              </Typography>
              {data.relations.length > 0 ? (
                <Box
                  component="ul"
                  className={classes.relationsList}
                  aria-label="Asset relations"
                >
                  {data.relations.map((relation: CloudHealthAssetRelation) => (
                    <Box
                      key={relation.name}
                      component="li"
                      className={classes.relationItem}
                    >
                      <Box className={classes.relationHeader}>
                        <Typography
                          component="span"
                          className={classes.relationName}
                        >
                          {relation.name}
                        </Typography>
                        <span
                          className={classes.relationBadge}
                          style={{
                            color: getBadgeColor(theme, relation.object_type),
                          }}
                          title={`Object type: ${relation.object_type}`}
                        >
                          {relation.object_type}
                        </span>
                      </Box>
                      {renderRelationItems(relation, classes)}
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" className={classes.apiEmpty}>
                  No relations returned.
                </Typography>
              )}
            </Box>
          </>
        ) : null}
      </AccordionDetails>
    </Accordion>
  );
}

type AssetInfoDrawerProps = {
  open: boolean;
  row?: AssetTableRow;
  onClose: () => void;
  initialAttributesExpanded?: boolean;
};

export function AssetInfoDrawer({
  open,
  row,
  onClose,
  initialAttributesExpanded = false,
}: AssetInfoDrawerProps) {
  const classes = useStyles();

  const asset = row?.asset;
  const status = asset?.assetStatus;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      classes={{ paper: classes.drawerPaper }}
      ModalProps={{ "aria-labelledby": DRAWER_TITLE_ID }}
    >
      <Box className={classes.content} role="document">
        <Box className={classes.header}>
          <Typography className={classes.eyebrow}>
            CloudHealth asset info
          </Typography>
          <Typography id={DRAWER_TITLE_ID} variant="h5">
            {row?.assetLabel ?? "Asset details"}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {row
              ? `${asset?.cloud ?? "Unknown provider"} · ${row.serviceLabel}`
              : "Select an asset to view details"}
          </Typography>
        </Box>

        <Box className={classes.body}>
          <Paper elevation={0} className={classes.section}>
            <Typography variant="subtitle2" className={classes.sectionTitle}>
              Summary
            </Typography>

            <Box component="dl" className={classes.summaryList}>
              <Typography
                component="dt"
                variant="body2"
                className={classes.term}
              >
                Name
              </Typography>
              <Typography
                component="dd"
                variant="body2"
                className={classes.value}
              >
                {row?.assetLabel ?? "Not available"}
              </Typography>

              <Typography
                component="dt"
                variant="body2"
                className={classes.term}
              >
                Provider
              </Typography>
              <Typography
                component="dd"
                variant="body2"
                className={classes.value}
              >
                {asset?.cloud ?? "Not available"}
              </Typography>

              <Typography
                component="dt"
                variant="body2"
                className={classes.term}
              >
                {scopeLabelFromProvider(asset?.cloud)}
              </Typography>
              <Typography
                component="dd"
                variant="body2"
                className={classes.value}
              >
                {row?.accountLabel ?? "Not available"}
              </Typography>

              <Typography
                component="dt"
                variant="body2"
                className={classes.term}
              >
                Service
              </Typography>
              <Typography
                component="dd"
                variant="body2"
                className={classes.value}
              >
                {row?.serviceName ?? row?.serviceLabel ?? "Not available"}
              </Typography>

              <Typography
                component="dt"
                variant="body2"
                className={classes.term}
              >
                Category
              </Typography>
              <Typography
                component="dd"
                variant="body2"
                className={classes.value}
              >
                {row?.category || "Not available"}
              </Typography>

              <Typography
                component="dt"
                variant="body2"
                className={classes.term}
              >
                Asset type
              </Typography>
              <Typography
                component="dd"
                variant="body2"
                className={classes.value}
              >
                {asset?.assetType ?? "Not available"}
              </Typography>

              <Typography
                component="dt"
                variant="body2"
                className={classes.term}
              >
                Status
              </Typography>
              <Typography
                component="dd"
                variant="body2"
                className={classes.value}
              >
                <span className={classes.statusValue}>
                  <StatusChip status={status} />
                  {!status && "No status available"}
                </span>
              </Typography>

              <Typography
                component="dt"
                variant="body2"
                className={classes.term}
              >
                Location / resource group
              </Typography>
              <Typography
                component="dd"
                variant="body2"
                className={classes.value}
              >
                {locationOrResourceGroup(row)}
              </Typography>

              <Typography
                component="dt"
                variant="body2"
                className={classes.term}
              >
                Asset ref
              </Typography>
              <Typography
                component="dd"
                variant="body2"
                className={classes.value}
              >
                {asset?.nodeRef ? (
                  <span className={classes.nodeRefRow}>
                    <span className={classes.nodeRefText}>
                      {truncateNodeRef(asset.nodeRef)}
                    </span>
                    <Tooltip title="Copy asset ref">
                      <IconButton
                        size="small"
                        aria-label={`Copy asset reference for ${row?.assetLabel ?? "asset"}`}
                        onClick={() => {
                          if (asset.nodeRef) {
                            navigator.clipboard.writeText(asset.nodeRef);
                          }
                        }}
                      >
                        <FileCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </span>
                ) : (
                  "Not available"
                )}
              </Typography>
            </Box>
          </Paper>

          {asset?.nodeRef && (
            <CloudHealthApiFieldsSection
              nodeRef={asset.nodeRef}
              api={explorerApiRef}
              initialExpanded={initialAttributesExpanded}
            />
          )}
        </Box>

        <Divider />
        <Box className={classes.footer}>
          <Button onClick={onClose} aria-label="Close asset info drawer">
            Close
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
}
